import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";

/**
 * WIL26D2C-263 — Doogma Customizer
 *
 * On-PDP personalization component for golf/volleyball products. Renders text or image mode
 * (see blocks/doogma-customizer.liquid), keeps line-item properties in sync, sanitizes imprint
 * text, projects the Doogma visualization over the first gallery image, and exposes an async
 * `saveDesign()` that product-form.js awaits before the Shopify add-to-cart.
 *
 * Doogma is a third-party black box. All Doogma-specific identifiers are centralized here so a
 * corrected contract is a one-line change.
 */

// Doogma loader, per client integration doc. Loaded once per page.
const DOOGMA_LOADER_SRC = "https://cdne2im.doogma.com/smartmobile-v2/loader.js";

// Reject saveDesign if Doogma never fires its success callback, so the shopper is never stuck.
const SAVE_DESIGN_TIMEOUT_MS = 30000;

// Allowed imprint characters: letters, digits, and spaces.
const ALLOWED_CHARS = /[^a-zA-Z0-9 ]/g;
// Strip HTML numeric entities (e.g. &#33;) and pictographic emoji. `\p{Emoji}` also matches digits,
// so we use Extended_Pictographic to avoid stripping valid numbers.
const HTML_ENTITY = /&#x?[0-9a-f]+;?/gi;
const EMOJI = /\p{Extended_Pictographic}/gu;

/**
 * @typedef {object} DoogmaRefs
 * @property {HTMLInputElement} [line1]
 * @property {HTMLInputElement} [line2]
 * @property {HTMLInputElement} [line3]
 * @property {HTMLInputElement} [propColor]
 * @property {HTMLInputElement} [propNumOfLines]
 * @property {HTMLInputElement} [propUploadedImage]
 * @property {HTMLInputElement} [ackCheckbox]
 * @property {HTMLElement} [selectedColorName]
 * @property {HTMLElement} [plugin]
 */

/**
 * @extends {Component<DoogmaRefs>}
 */
class DoogmaCustomizer extends Component {
  #abortController = new AbortController();

  /** Current `.doogmaplugin` element. Section morphs can replace the ref node. */
  #pluginEl = /** @type {HTMLElement | null} */ (null);

  /** @returns {'text' | 'image'} */
  get #mode() {
    return this.dataset.mode === "text" ? "text" : "image";
  }

  connectedCallback() {
    super.connectedCallback();

    this.#pluginEl = this.refs.plugin ?? null;

    const section = this.closest(".shopify-section, dialog");
    section?.addEventListener(
      ThemeEvents.variantUpdate,
      this.#onVariantUpdate,
      {
        signal: this.#abortController.signal,
      },
    );

    if (this.#mode === "text") {
      this.#syncColorProperty();
      this.#updateNumOfLines();
    }

    this.#whenPageReady(() => this.#initDoogma());
  }

  /**
   * Runs `callback` once the document has finished loading (or immediately if it already has).
   * @param {() => void} callback
   */
  #whenPageReady(callback) {
    if (document.readyState === "complete") {
      callback();
      return;
    }
    window.addEventListener("load", () => callback(), {
      once: true,
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Whether this product is a Doogma product (the block only renders when it is).
   * @returns {boolean}
   */
  isActive() {
    return true;
  }

  /**
   * Sanitizes imprint text on input and keeps the line count in sync. The input doubles as the
   * Shopify line-item property and the Doogma source (Doogma reads it by class).
   * @param {Event} event
   */
  handleTextInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    const sanitized = this.#sanitize(input.value);
    if (sanitized !== input.value) {
      const caret = input.selectionStart ?? sanitized.length;
      input.value = sanitized;
      const nextCaret = Math.min(caret, sanitized.length);
      input.setSelectionRange(nextCaret, nextCaret);
    }

    if (input.value.trim() !== "") input.removeAttribute("aria-invalid");
    this.#updateNumOfLines();
  }

  /**
   * Shopper-facing reason Add to Bag should wait. Empty when the text customizer is ready.
   * EvoShield only (`data-require-imprint-color`). Wilson keeps the live add path:
   * the first line stays a native required field, and imprint color stays optional.
   * The first rendered line is required. Imprint color is required only when swatches exist.
   * @returns {string}
   */
  getValidationMessage() {
    this.#clearFieldValidity();
    if (this.#mode !== "text" || this.dataset.requireImprintColor == null) return "";

    const requiredLine = this.querySelector("input.doogma-customizer__input[required]");
    if (requiredLine instanceof HTMLInputElement && requiredLine.value.trim() === "") {
      requiredLine.setAttribute("aria-invalid", "true");
      const field = requiredLine.dataset.fieldLabel || "Top";
      const template = this.dataset.msgLineRequired || "The __FIELD__ text field is required";
      return template.split("__FIELD__").join(field);
    }

    const colorInputs = this.querySelectorAll(".doogma-color");
    if (colorInputs.length > 0 && !this.querySelector(".doogma-color:checked")) {
      return this.dataset.msgColorRequired || "Please select an imprint color.";
    }

    return "";
  }

  /**
   * Mirrors the selected imprint color NAME into the cart property.
   * @param {Event} event
   */
  handleColorChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    this.#syncColorProperty();
  }

  /**
   * Runs the Doogma save before add-to-cart. Resolves once Doogma has populated the machine
   * fields (by class); rejects with a translated message if Doogma is unavailable, the image is
   * missing, or saving fails/times out.
   * @returns {Promise<void>}
   */
  async saveDesign() {
    const validationMessage = this.getValidationMessage();
    if (validationMessage) throw new Error(validationMessage);

    const doogma =
      /** @type {{ saveDesign?: (opts: { success: () => void, error?: () => void }) => void } | undefined} */ (
        // @ts-ignore - injected by the Doogma loader
        window.doogma
      );

    if (!doogma || typeof doogma.saveDesign !== "function") {
      throw new Error(
        this.dataset.msgSaveFailed || "Could not save your customization.",
      );
    }

    if (this.#mode === "image" && !this.#hasUploadedImage()) {
      throw new Error(
        this.dataset.msgImageRequired || "Please upload an image.",
      );
    }

    await new Promise((resolve, reject) => {
      const timeoutId = setTimeout(() => {
        reject(
          new Error(
            this.dataset.msgSaveFailed || "Could not save your customization.",
          ),
        );
      }, SAVE_DESIGN_TIMEOUT_MS);

      const finish = (
        /** @type {(value?: unknown) => void} */ done,
        /** @type {Error=} */ error,
      ) => {
        clearTimeout(timeoutId);
        error ? reject(error) : done();
      };

      try {
        doogma.saveDesign({
          success: () => finish(resolve),
          error: () =>
            finish(
              reject,
              new Error(
                this.dataset.msgSaveFailed ||
                  "Could not save your customization.",
              ),
            ),
        });
      } catch (caught) {
        finish(
          reject,
          caught instanceof Error ? caught : new Error(String(caught)),
        );
      }
    });

    if (this.#mode === "text") this.#updateNumOfLines();
  }

  /** @returns {boolean} */
  #hasUploadedImage() {
    return Boolean(this.refs.propUploadedImage?.value?.trim());
  }

  #clearFieldValidity() {
    const invalid = this.querySelector("input.doogma-customizer__input[aria-invalid='true']");
    invalid?.removeAttribute("aria-invalid");
  }

  /** @param {string} value @returns {string} */
  #sanitize(value) {
    return value
      .replace(HTML_ENTITY, "")
      .replace(EMOJI, "")
      .replace(ALLOWED_CHARS, "");
  }

  #syncColorProperty() {
    const { propColor } = this.refs;
    const selected = this.querySelector(".doogma-color:checked");
    const colorName =
      selected instanceof HTMLElement ? (selected.dataset.colorName ?? "") : "";
    if (propColor) propColor.value = colorName;
    if (this.refs.selectedColorName)
      this.refs.selectedColorName.textContent = colorName;
  }

  #updateNumOfLines() {
    const { propNumOfLines } = this.refs;
    if (!propNumOfLines) return;
    const lines = [this.refs.line1, this.refs.line2, this.refs.line3];
    const count = lines.filter(
      (line) => line instanceof HTMLInputElement && line.value.trim() !== "",
    ).length;
    propNumOfLines.value = count > 0 ? String(count) : "";
  }

  /**
   * Brings up Doogma for the current product: ensure the loader, place the plugin in the first
   * gallery cell, then drive it to render.
   */
  #initDoogma() {
    this.#pluginEl = this.refs.plugin ?? this.#pluginEl;
    this.#projectPluginIntoGallery();
    this.#loadDoogmaLoader();
    this.#ensureRendered();
  }

  /**
   * Loads the Doogma loader script once per page (guards against the combined-listing `main` morph
   * re-injecting it).
   */
  #loadDoogmaLoader() {
    if (document.querySelector("script[data-doogma-loader]")) return;
    const script = document.createElement("script");
    script.src = DOOGMA_LOADER_SRC;
    script.async = true;
    script.dataset.doogmaLoader = "true";
    document.head.appendChild(script);
  }

  /**
   * @returns {boolean} Whether Doogma has actually mounted its visualization. We check for the
   * `<canvas>` specifically — Doogma adds the `dd-doogma` class the instant it *starts* processing
   * (well before the canvas mounts), so that class is not a reliable "rendered" signal.
   */
  #isPluginRendered() {
    return Boolean(this.#pluginEl?.querySelector("canvas"));
  }

  /** Hides the static base image once the Doogma canvas has mounted (its cell now has real size). */
  #markRendered() {
    const cell = this.#pluginEl?.parentElement;
    if (cell instanceof HTMLElement)
      cell.setAttribute("data-doogma-rendered", "true");
  }

  /**
   * Drives Doogma to render into the plugin and keeps retrying until it does.
   *
   * The loader's initial scan handles first load. `reReadEmbedCode()` handles section morphs after
   * variant/product changes. Stop as soon as the canvas mounts.
   *
   * Before calling `reReadEmbedCode()` for the first time we wait for the gallery cell to have a
   * stable size across two consecutive measurements. Doogma reads the container dimensions once
   * during initialization and positions its internal elements (including the upload area) based on
   * those coordinates — if we hand it a half-laid-out container it will place its controls
   * incorrectly and no amount of CSS rescaling can fix that.
   * @param {number} [attempt]
   */
  #ensureRendered(attempt = 0) {
    if (!this.isConnected) return;

    if (this.#isPluginRendered()) {
      this.#markRendered();
      return;
    }

    const loader = /** @type {{ reReadEmbedCode?: () => void } | undefined} */ (
      /** @type {any} */ (window).doogmaLoader
    );
    const loaderReady = Boolean(
      loader && typeof loader.reReadEmbedCode === "function",
    );

    setTimeout(
      () => this.#ensureRendered(attempt + 1),
      loaderReady ? 700 : 250,
    );
  }

  /**
   * @param {HTMLElement} element
   * @param {Element | null | undefined} root
   * @returns {boolean}
   */
  #isVisibleMediaContainer(element, root) {
    for (
      let current = element;
      current && current !== root;
      current = current.parentElement
    ) {
      const style = getComputedStyle(current);
      if (style.display === "none" || style.visibility === "hidden")
        return false;
    }

    return element.getClientRects().length > 0;
  }

  /**
   * Places the `.doogmaplugin` live preview into the first MAIN gallery cell, excluding the
   * zoom-dialog's cloned containers. Re-run after combined-listing variant changes.
   */
  #projectPluginIntoGallery() {
    const plugin = this.#pluginEl;
    if (!plugin) return;

    const section = this.closest(".shopify-section, dialog");
    const gallery = section?.querySelector("media-gallery") ?? section;
    const candidates = Array.from(
      gallery?.querySelectorAll(".product-media-container") ?? [],
    ).filter(
      (el) =>
        el instanceof HTMLElement && !el.closest(".dialog-zoomed-gallery"),
    );
    const target = candidates.find((el) =>
      this.#isVisibleMediaContainer(el, gallery),
    );

    if (!(target instanceof HTMLElement)) return;

    // Clear any stale host marker left on another cell (morph / re-projection safety).
    gallery?.querySelectorAll(".doogma-media-host").forEach((el) => {
      if (el !== target) el.classList.remove("doogma-media-host");
    });

    target.classList.add("doogma-media-host");
    plugin.hidden = false;
    if (plugin.parentElement !== target) {
      target.appendChild(plugin);
    }
  }

  #onVariantUpdate = () => {
    // Defer so the variant-picker/product-form morph of `main` settles before we re-project.
    queueMicrotask(() => {
      this.#pluginEl = this.refs.plugin ?? this.#pluginEl;
      this.#projectPluginIntoGallery();

      // Remove the rendered marker so the product image shows behind the plugin while Doogma
      // re-renders. We cannot call #ensureRendered() immediately here because the canvas element
      // persists between renders — #isPluginRendered() would return true right away and
      // #markRendered() would re-add the attribute before Doogma finishes updating the canvas.
      // Instead we defer the check past Doogma's ~2s re-render cycle.
      const host = this.#pluginEl?.parentElement;
      if (host instanceof HTMLElement) {
        host.removeAttribute("data-doogma-rendered");
      }
      setTimeout(() => this.isConnected && this.#ensureRendered(), 2500);

      if (this.#mode === "text") {
        this.#syncColorProperty();
        this.#updateNumOfLines();
      }
    });
  };
}

if (!customElements.get("doogma-customizer-component")) {
  customElements.define("doogma-customizer-component", DoogmaCustomizer);
}

export { DoogmaCustomizer };
